import React, { useRef, useState } from "react";
import { Box, ButtonBase, Typography } from "@mui/material";
import inkfreeLogo from "assets/images/side_nav_inkfree_logo.svg";
import brandingStyles from "../Branding.styles";

const styles = brandingStyles;

interface LogoUploadProps {
  /** Hint line shown beside the drop target. */
  hint: string;
  imageUrl?: string;
  onUpload: (file: File) => void;
  onRemove: () => void;
  disabled?: boolean;
}

export const getRenderableLogoUrl = (url: string | undefined) => {
  if (!url) return "";
  if (
    url.startsWith("blob:") ||
    url.startsWith("http:") ||
    url.startsWith("https:") ||
    url.startsWith("data:")
  ) {
    return url;
  }
  return "";
};

const ACCEPTED_TYPES = ["image/jpeg", "image/png"];
const MAX_SIZE = 1 * 1024 * 1024;

/**
 * Dashed 150×66 drop target with the format hint and Replace / Remove links
 * beside it, exactly as in the Branding design. Falls back to the Inkfree mark
 * so the box is never empty — the same logo the signing emails use by default.
 */
const LogoUpload: React.FC<LogoUploadProps> = ({
  hint,
  imageUrl,
  onUpload,
  onRemove,
  disabled = false,
}) => {
  const inputRef = useRef<HTMLInputElement>(null);
  const [error, setError] = useState<string | null>(null);

  const customLogo = getRenderableLogoUrl(imageUrl);

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    // Reset so picking the same file twice still fires a change event.
    event.target.value = "";
    if (!file) return;
    if (!ACCEPTED_TYPES.includes(file.type)) {
      setError("Supported formats: PNG, JPG");
      return;
    }
    if (file.size > MAX_SIZE) {
      setError("Maximum file size: 1 MB");
      return;
    }
    setError(null);
    onUpload(file);
  };

  const openFilePicker = () => {
    if (disabled) return;
    inputRef.current?.click();
  };

  const handleRemove = () => {
    setError(null);
    onRemove();
  };

  return (
    <Box sx={styles.logoRow}>
      <ButtonBase
        disableRipple
        aria-label="Upload company logo"
        disabled={disabled}
        onClick={openFilePicker}
        sx={styles.logoBox}
      >
        <Box
          component="img"
          src={customLogo || inkfreeLogo}
          alt="Company logo"
          sx={styles.logoImage}
        />
      </ButtonBase>

      <Box sx={styles.logoInfo}>
        <Box component="span">{hint}</Box>
        <Box sx={styles.logoLinkRow}>
          <ButtonBase
            disableRipple
            disabled={disabled}
            onClick={openFilePicker}
            sx={styles.logoLink("replace")}
          >
            Replace
          </ButtonBase>
          {!!customLogo && (
            <ButtonBase
              disableRipple
              disabled={disabled}
              onClick={handleRemove}
              sx={styles.logoLink("remove")}
            >
              Remove
            </ButtonBase>
          )}
        </Box>
        {error && (
          <Typography component="span" role="alert" sx={styles.logoError}>
            {error}
          </Typography>
        )}
      </Box>

      <input
        ref={inputRef}
        type="file"
        accept=".png,.jpg,.jpeg"
        aria-label="Upload company logo"
        tabIndex={-1}
        hidden
        onChange={handleFileChange}
      />
    </Box>
  );
};

export default LogoUpload;
